import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { ConsultationService } from "../services/consultationService";
import { idbGet, idbPut } from "@utils/idb";
import { sanitizeText } from "@utils/sanitize";

export const useConsultationManagement = (backendUrl) => {
  const [groupedConsultations, setGroupedConsultations] = useState([]); // Initialize as empty array
  const [pendingGroups, setPendingGroups] = useState([]);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("appointments");
  const mountedRef = useRef(true);

  const consultationService = useMemo(
    () => new ConsultationService(backendUrl),
    [backendUrl]
  );
  // put near the top
  const eqId = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

  const fetchConsultations = useCallback(async () => {
    try {
      console.log("🔄 fetchConsultations: Calling backend...");
      const data = await consultationService.fetchConsultations();

      console.log("📊 fetchConsultations: Raw backend response:", data);

      if (mountedRef.current) {
        const consultations = Array.isArray(data) ? data : [];

        // Log timestamp information for debugging
        consultations.forEach((group, groupIndex) => {
          group.files?.forEach((file, fileIndex) => {
            console.log(`🕐 Group ${groupIndex}, File ${fileIndex}:`, {
              file_id: file.file_id,
              file_name: file.file_name,
              recording_id: file.recording_id,
              local_datetime: file.local_datetime,
              created_at: file.created_at,
              template_id: file.template_id,
            });
          });
        });

        setGroupedConsultations(consultations);
      }
    } catch (e) {
      console.error("Failed to load consultations:", e);
      if (mountedRef.current) {
        setGroupedConsultations([]);
      }
    }
  }, [consultationService]);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      const saved = await idbGet("ui", "pendingGroups");
      if (mountedRef.current && Array.isArray(saved)) setPendingGroups(saved);
    })();
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!selectedConsultation?.recording_id) return;
    const rid = String(selectedConsultation.recording_id);

    const updated = (
      Array.isArray(groupedConsultations) ? groupedConsultations : []
    )
      .flatMap((g) => (g.files || []).map((f) => ({ ...f, date: g.date })))
      .find((f) => eqId(f.recording_id, rid));

    if (updated) {
      const withSib = withSiblings(updated);
      setSelectedConsultation(withSib);
      setViewMode(updated.isPlaceholder ? "processing" : "consultation");
    }
  }, [groupedConsultations]); // deps are fine since we only set selection here

  const allFilesFlat = useMemo(() => {
    // Add safety check - ensure groupedConsultations is an array
    if (!Array.isArray(groupedConsultations)) {
      console.warn(
        "groupedConsultations is not an array:",
        groupedConsultations
      );
      return [];
    }

    return groupedConsultations.flatMap((g) =>
      (g.files || []).map((f) => ({ ...f, date: g.date }))
    );
  }, [groupedConsultations]);

  const withSiblings = useCallback(
    (file) => {
      if (!file) return null;

      // Add safety check for allFilesFlat
      if (!Array.isArray(allFilesFlat)) {
        console.warn("allFilesFlat is not an array:", allFilesFlat);
        return { ...file, __siblings: [] };
      }

      const siblings = allFilesFlat
        .filter((f) => eqId(f.recording_id, file.recording_id))
        .sort((a, b) => {
          const ka = a.created_at || a.file_name || "";
          const kb = b.created_at || b.file_name || "";
          return String(ka).localeCompare(String(kb));
        });
      return { ...file, __siblings: siblings };
    },
    [allFilesFlat]
  );

  const formatLocalDateTime = useCallback(() => {
    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "short" });
    const day = now.toLocaleString("en-US", { day: "numeric" });
    const year = now.toLocaleString("en-US", { year: "numeric" });
    const time = now.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${month} ${day}, ${year} at ${time}`;
  }, []);

  const todayKey = useCallback(() => new Date().toISOString().slice(0, 10), []);

  const addProcessingPlaceholder = useCallback(
    (sessionId, createdAtISO) => {
      const fileName = `${formatLocalDateTime()} - processing... `;
      const placeholder = {
        file_id: `placeholder-${sessionId}`,
        file_name: fileName,
        template_name: "",
        recording_id: sessionId,
        isPlaceholder: true,
        status: "processing",
        created_at: createdAtISO || new Date().toISOString(),
      };
      const group = { date: todayKey(), files: [placeholder] };
      setPendingGroups((prev) => {
        const next = [group, ...(Array.isArray(prev) ? prev : [])];
        idbPut("ui", "pendingGroups", next);
        return next;
      });
    },
    [formatLocalDateTime, todayKey]
  );

  const removeProcessingPlaceholder = useCallback((sessionId) => {
    setPendingGroups((prev) => {
      const next = (prev || [])
        .map((g) => ({
          ...g,
          files: (g.files || []).filter(
            (f) => !(f.isPlaceholder && eqId(f.recording_id, sessionId))
          ),
        }))
        .filter((g) => (g.files || []).length > 0);
      idbPut("ui", "pendingGroups", next);
      return next;
    });
  }, []);

  const deleteConsultation = useCallback(
    async (item) => {
      try {
        if (item.isPlaceholder) {
          removeProcessingPlaceholder(item.recording_id);
          if (eqId(selectedConsultation?.file_id, item.file_id)) {
            setSelectedConsultation(null);
            setViewMode("new");
          }
          return;
        }

        // Get all files with the same recording_id (siblings)
        const itemWithSiblings = withSiblings(item);
        const filesToDelete = (itemWithSiblings.__siblings?.length ? itemWithSiblings.__siblings : [item]);
        
        console.log(`🗑️ Deleting consultation group with ${filesToDelete.length} files:`, 
          filesToDelete.map(f => ({ file_id: f.file_id, file_name: f.file_name }))
        );

        // Delete all files in the consultation group
        for (const file of filesToDelete) {
          await consultationService.deleteFile(file.file_id);
        }

        // Remove the entire consultation group from state
        setGroupedConsultations((prev) => {
          if (!Array.isArray(prev)) return [];

          return prev
            .map((group) => ({
              ...group,
              files: (group.files || []).filter(
                (f) => !eqId(f.recording_id, item.recording_id)
              ),
            }))
            .filter((g) => (g.files || []).length > 0);
        });

        // Clear selection if any of the deleted files was selected
        if (eqId(selectedConsultation?.recording_id, item.recording_id)) {
          setSelectedConsultation(null);
          setViewMode("new");
        }
      } catch (err) {
        console.error("Failed to delete consultation:", err);
        throw new Error("Error deleting consultation. Please try again.");
      }
    },
    [
      consultationService,
      removeProcessingPlaceholder,
      selectedConsultation,
      setViewMode,
      withSiblings,
      setGroupedConsultations,
    ]
  );
  const renameConsultation = useCallback(
    async (item, newName) => {
      const trimmed = sanitizeText((newName ?? "").trim());
      if (!trimmed) {
        throw new Error("Name cannot be empty");
      }

      const recordingKey = String(item.recording_id ?? "");

      // 1) Update pendingGroups (placeholders / offline)
      setPendingGroups((prev) => {
        const next = (prev || []).map((group) => ({
          ...group,
          files: (group.files || []).map((f) =>
            eqId(f.recording_id, recordingKey)
              ? { ...f, file_name: trimmed }
              : f
          ),
        }));
        idbPut("ui", "pendingGroups", next);
        return next;
      });

      // 2) Find all persisted siblings for this consultation
      const itemWithSiblings = withSiblings(item);
      const filesToRename = itemWithSiblings?.__siblings?.length
        ? itemWithSiblings.__siblings
        : item.file_id
        ? [item]
        : [];

      // 3) Call backend rename for each persisted file
      for (const file of filesToRename) {
        if (!file.isPlaceholder && file.file_id) {
          const data = { name: trimmed };
            await consultationService.renameFile(file.file_id, data);
        }
      }

      // 4) Update groupedConsultations in state (all files in this consultation)
      setGroupedConsultations((prev) => {
        if (!Array.isArray(prev)) return [];
        return prev.map((group) => ({
          ...group,
          files: (group.files || []).map((f) =>
            eqId(f.recording_id, recordingKey)
              ? { ...f, file_name: trimmed }
              : f
          ),
        }));
      });

      // 5) Update selectedConsultation + its siblings if current selection belongs to this consultation
      if (eqId(selectedConsultation?.recording_id, recordingKey)) {
        setSelectedConsultation((prev) =>
          prev
            ? {
                ...prev,
                file_name: trimmed,
                __siblings: (prev.__siblings || []).map((f) => ({
                  ...f,
                  file_name: trimmed,
                })),
              }
            : prev
        );
      }
    },
    [
      consultationService,
      withSiblings,
      selectedConsultation,
      setGroupedConsultations,
      setPendingGroups,
      setSelectedConsultation,
    ]
  );

  const selectConsultation = useCallback(
    (item) => {
      const withSib = withSiblings(item);
      setSelectedConsultation(withSib);
      setViewMode(item?.isPlaceholder ? "processing" : "consultation");
    },
    [withSiblings, setViewMode]
  );

  const combinedConsultations = useMemo(() => {
    const pending = Array.isArray(pendingGroups) ? pendingGroups : [];
    const grouped = Array.isArray(groupedConsultations)
      ? groupedConsultations
      : [];
    return [...pending, ...grouped];
  }, [pendingGroups, groupedConsultations]);

  return {
    // State
    groupedConsultations,
    pendingGroups,
    selectedConsultation,
    viewMode,
    combinedConsultations,

    // Actions
    setGroupedConsultations,
    setPendingGroups,
    setViewMode,
    setSelectedConsultation,
    fetchConsultations,
    addProcessingPlaceholder,
    removeProcessingPlaceholder,
    deleteConsultation,
    selectConsultation,
    withSiblings,
    formatLocalDateTime,
    renameConsultation,
  };
};
